import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DeletionDialog from "../../../library/Dialog/DeletionDialog";

const DisclosureTagLibraryDialog = ({
  open,
  onClose,
  tagsLoading,
  tagsSubmitting,
  filteredTags,
  search,
  setSearch,
  newTagInput,
  setNewTagInput,
  onCreateTag,
  editingTagId,
  editingTagName,
  setEditingTagName,
  onStartEditTag,
  onSaveEditTag,
  onCancelEditTag,
  onDeleteTag,
  onBatchDeleteTags,
  tagUsageMap = {},
}) => {
  const theme = useTheme();
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchConfirmOpen, setBatchConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setBatchConfirmOpen(false);
    }
  }, [open]);

  const deletableSelectedIds = useMemo(() => {
    return selectedIds.filter((id) => {
      const tag = filteredTags.find((t) => t.t_id === id);
      if (!tag || tag.is_system) return false;
      if (tag.organization_id == null) return false;
      return true;
    });
  }, [selectedIds, filteredTags]);

  const batchDeletePreviewTitles = useMemo(() => {
    if (!deletableSelectedIds.length) return [];
    const idSet = new Set(deletableSelectedIds.map(String));
    return filteredTags
      .filter((t) => idSet.has(String(t.t_id)))
      .map((t) => t.name || "")
      .slice(0, 5);
  }, [deletableSelectedIds, filteredTags]);
  const combinedInput = search || "";
  const normalizedNewTag = combinedInput.trim();
  const hasInvalidChars = /[,\s]/.test(combinedInput);
  const exceedsMaxLength = normalizedNewTag.length > 20;
  const alreadyExists = filteredTags.some(
    (tag) => (tag.name || "").toLowerCase() === normalizedNewTag.toLowerCase()
  );
  const hasError = Boolean(
    normalizedNewTag && (hasInvalidChars || exceedsMaxLength || alreadyExists)
  );

  const helperText = hasError
    ? alreadyExists
      ? "Tag already exists. Showing matched result below."
      : exceedsMaxLength
        ? "Max 20 characters"
        : "Use dash instead of space/comma"
    : normalizedNewTag
      ? 'Press "Enter" to add if no exact existing tag.'
      : "Search existing tags or type to create a new one.";

  const handleCreateInlineTag = () => {
    if (!normalizedNewTag || hasError || tagsSubmitting) return;
    setNewTagInput(normalizedNewTag);
    onCreateTag();
  };

  const { customTags, systemTags } = useMemo(() => {
    const custom = [];
    const system = [];
    filteredTags.forEach((tag) => {
      if (tag.is_system) system.push(tag);
      else custom.push(tag);
    });
    return { customTags: custom, systemTags: system };
  }, [filteredTags]);

  const toggleSelect = (t_id) => {
    const tag = filteredTags.find((t) => t.t_id === t_id);
    if (tag?.is_system || tag?.organization_id == null) return;
    setSelectedIds((prev) =>
      prev.includes(t_id) ? prev.filter((id) => id !== t_id) : [...prev, t_id]
    );
  };

  const handleOpenBatchDeleteConfirm = () => {
    if (!deletableSelectedIds.length || !onBatchDeleteTags || tagsSubmitting)
      return;
    setBatchConfirmOpen(true);
  };

  const handleConfirmBatchDelete = async () => {
    if (!onBatchDeleteTags || !deletableSelectedIds.length) return;
    try {
      await onBatchDeleteTags(deletableSelectedIds);
      setBatchConfirmOpen(false);
      setSelectedIds([]);
    } catch {
      /* parent shows error */
    }
  };

  const handleBatchDeletionClose = () => {
    if (!tagsSubmitting) setBatchConfirmOpen(false);
  };

  const renderTagCard = (tag, { readonly = false } = {}) => {
    const isEditing = editingTagId === tag.t_id;
    const isSystem = Boolean(tag.is_system);
    const usage = tagUsageMap[String(tag.t_id)] || 0;
    const selected = selectedIds.includes(tag.t_id);

    return (
      <Box
        key={tag.t_id}
        sx={{
          borderRadius: 2,
          px: 1.25,
          py: 1,
          bgcolor: readonly
            ? "action.hover"
            : (theme) => alpha(theme.palette.primary.main, 0.04),
          border: "1px solid",
          borderColor: readonly ? "divider" : "transparent",
          transition: "all .18s ease",
          "&:hover": {
            bgcolor: readonly
              ? "action.selected"
              : (theme) => alpha(theme.palette.primary.main, 0.1),
          },
        }}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <Checkbox
            size='small'
            checked={selected}
            onChange={() => toggleSelect(tag.t_id)}
            disabled={readonly}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {isEditing ? (
              <TextField
                size='small'
                value={editingTagName}
                onChange={(e) => setEditingTagName(e.target.value)}
                fullWidth
              />
            ) : (
              <Stack
                direction='row'
                spacing={0.75}
                alignItems='center'
                flexWrap='wrap'
              >
                <Chip
                  size='small'
                  label={tag.name}
                  variant={readonly ? "filled" : "outlined"}
                />
                <Chip
                  size='small'
                  label={`Used ${usage} times`}
                  variant='outlined'
                  color={usage > 0 ? "primary" : "default"}
                />
                {isSystem ? (
                  <Chip
                    size='small'
                    label='System'
                    color='secondary'
                    variant='outlined'
                  />
                ) : null}
              </Stack>
            )}
          </Box>

          {!readonly && (
            <Stack direction='row' spacing={0.25}>
              {isEditing ? (
                <>
                  <Tooltip title='Save'>
                    <span>
                      <IconButton
                        size='small'
                        color='primary'
                        onClick={onSaveEditTag}
                        disabled={!editingTagName.trim() || tagsSubmitting}
                      >
                        <CheckIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='Cancel'>
                    <IconButton size='small' onClick={onCancelEditTag}>
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip title='Edit tag'>
                    <span>
                      <IconButton
                        size='small'
                        onClick={() => onStartEditTag(tag)}
                        disabled={tagsSubmitting}
                      >
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='Delete tag'>
                    <span>
                      <IconButton
                        size='small'
                        color='error'
                        onClick={() => onDeleteTag(tag)}
                        disabled={tagsSubmitting}
                      >
                        <DeleteOutlineIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <Dialog open={open} maxWidth='sm' fullWidth>
        <Box
          sx={{
            p: 2.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Box
              sx={{
                p: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                borderRadius: 2,
              }}
            >
              <LocalOfferIcon />
            </Box>
            <Box>
              <Typography variant='h6' fontWeight={700}>
                Disclosure Tags
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Manage the tags for your disclosures.
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            size='small'
            sx={{ color: "text.secondary" }}
            disabled={tagsSubmitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {deletableSelectedIds.length > 0 && onBatchDeleteTags ? (
          <Toolbar
            variant='dense'
            sx={{
              pl: 2,
              pr: 1,
              minHeight: 48,
              bgcolor: alpha(theme.palette.error.main, 0.06),
              borderBottom: "1px solid",
              borderColor: "divider",
              gap: 1,
            }}
          >
            <Typography variant='body2' sx={{ flex: 1 }}>
              {deletableSelectedIds.length} tag
              {deletableSelectedIds.length === 1 ? "" : "s"} selected
            </Typography>
            <Button
              size='small'
              color='error'
              startIcon={<DeleteOutlineIcon />}
              onClick={handleOpenBatchDeleteConfirm}
              disabled={tagsSubmitting}
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.2),
                },
              }}
            >
              Delete selected
            </Button>
          </Toolbar>
        ) : null}

        <DialogContent>
          <Stack spacing={2}>
            <TextField
              value={combinedInput}
              onChange={(e) => {
                setSearch(e.target.value);
                setNewTagInput(e.target.value);
              }}
              size='small'
              fullWidth
              error={hasError}
              placeholder='Search or create a tag (e.g., "Urgent", "Bug")'
              helperText={helperText}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreateInlineTag();
                }
              }}
              InputProps={{
                sx: {
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: hasError ? "error.main" : "divider",
                  bgcolor: "background.paper",
                },
                endAdornment: (
                  <Tooltip
                    title={
                      hasError
                        ? "Fix input issues first"
                        : normalizedNewTag
                          ? alreadyExists
                            ? "Tag already exists in results"
                            : "Press Enter or click to add"
                          : "Search or create"
                    }
                  >
                    <span>
                      <IconButton
                        size='small'
                        color={hasError ? "error" : "primary"}
                        onClick={handleCreateInlineTag}
                        disabled={
                          !normalizedNewTag || hasError || tagsSubmitting
                        }
                      >
                        {hasError ? (
                          <ErrorOutlineIcon fontSize='small' />
                        ) : normalizedNewTag && !alreadyExists ? (
                          <KeyboardReturnIcon fontSize='small' />
                        ) : (
                          <AddIcon fontSize='small' />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                ),
              }}
            />

            {tagsLoading ? (
              <Typography variant='body2' color='text.secondary'>
                Loading tags...
              </Typography>
            ) : filteredTags.length === 0 ? (
              <Box
                sx={{
                  height: { xs: 320, sm: 420, md: 480 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  No tags found.
                </Typography>
              </Box>
            ) : (
              <Stack
                spacing={2}
                sx={{
                  height: { xs: 320, sm: 420, md: 480 },
                  overflowY: "auto",
                  pr: 0.5,
                }}
              >
                <Box>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontWeight: 700 }}
                  >
                    All Tags
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 1, minHeight: 220 }}>
                    {customTags.length > 0 ? (
                      customTags.map((tag) => renderTagCard(tag))
                    ) : (
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 220,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography variant='body2' color='text.secondary'>
                          No tags found.
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* <Box>
                <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
                  System Tags
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {systemTags.length > 0 ? (
                    systemTags.map((tag) => renderTagCard(tag, { readonly: true }))
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      No system tags.
                    </Typography>
                  )}
                </Stack>
              </Box> */}
              </Stack>
            )}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: "background.default",
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "flex-end",
            borderRadius: 4,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          <Button onClick={onClose} disabled={tagsSubmitting}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <DeletionDialog
        open={batchConfirmOpen}
        onClose={handleBatchDeletionClose}
        title='Delete tags'
        count={deletableSelectedIds.length}
        itemSingular='tag'
        previewTitles={batchDeletePreviewTitles}
        previewLabel='Including'
        deleting={tagsSubmitting}
        onConfirm={handleConfirmBatchDelete}
        confirmLabel='Delete'
        warning={
          <Alert
            severity='warning'
            icon={<InfoOutlineIcon />}
            sx={{ fontSize: "0.875rem" }}
          >
            Tags will be removed from all disclosures that use them.
          </Alert>
        }
      />
    </>
  );
};

export default DisclosureTagLibraryDialog;
